import React from 'react';
import { RocketLaunchIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';

const TeamHealthChecksStage2 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 2: Support Facilitation Across All Teams" icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />} heading>
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-4">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen={true}>
          <ul className="list-none space-y-3">
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Identify and brief your facilitators (includes: building your team health check board)</span>
            </li>
            <li className="flex items-start">
              <span className="text-primary-light mr-2">—</span>
              <span>Co-facilitate/support each team's first health check (optional*) *depending on the facilitation skill/confidence of the folks you identify)</span>
            </li>
          </ul>
          <p className="mt-4">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Identify and brief your facilitators</h3>
        <p className="text-gray-200 mb-4">
          So in the last step we've co-created our health check. Now we want to run our first round…
        </p>

        <ExpandableCallout type="info" title="A quick note on cadence:" className="mt-4">
          <p className="text-gray-200">
            I usually recommend aiming to run a round of health checks either once every 6 weeks or once every quarter.
          </p>
        </ExpandableCallout>

        <p className="text-gray-200 mt-4">
          Depending on your organisation size, you might want to do this yourself or engage your people managers or get a volunteer facilitator from each of the teams.
        </p>

        <p className="text-gray-200 mt-4">
          Let's work on the assumption that you have multiple teams, either invite volunteers:
        </p>

        <CopyToNotion title="Slack Message Template" slack>
          <p>Hey folks, we're going to be running our first team health check in a couple of weeks. Can I have a volunteer from each team to work with me to facilitate yours?</p>
        </CopyToNotion>

        <p className="text-gray-200 mt-4">
          Or go to the lead/relevant people manager and confirm they are the best person to facilitate this.
        </p>

        <p className="text-gray-200 mt-4">
          Ideally we're looking for someone who is a dab hand with miro (or equivalent whiteboarding tool) and good at holding space / helping to include everyone.
        </p>

        <p className="text-gray-200 mt-4">
          Once you've got your facilitators…
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Action: Create Your Team Health Check Board</h3>
        <p className="text-gray-200 mb-4">
          Using the gauge cards you defined in Stage 1 you want to create a single board for all teams that looks like this:
        </p>

        <PlaybookImage src="https://auth.openorg.fyi/storage/v1/object/public/resources/team-health-check/miro-template-screenshot.png" alt="Team health check board example" />

        <div className="space-y-4 mt-4 mb-4">
          <ToolCallout
            tool="miro"
            label="Malcolm Bastien's Team Health Check Template"
            href="https://miro.com/miroverse/squad-health-check/?social=copy-link"
            description="A comprehensive template that you can quickly adapt to your needs"
          />
          
          <ToolCallout
            tool="miro"
            label="6-Team Organization Template"
            href="https://miro.com/app/board/uXjVKNXhihg=/?share_link_id=806683851962"
            description="A template adapted for a 6-team organization structure"
          />
        </div>

        <p className="text-gray-200 mb-4">
          You'll want to select all and duplicate each grid so that each team has one (but keep it all in the same board for transparency and ease of collecting insights later on)
        </p>

        <p className="text-gray-200 mb-4">
          It should look something like this (if you have 6 teams)
        </p>

        <PlaybookImage src="https://auth.openorg.fyi/storage/v1/object/public/resources/team-health-check/6-teams-miro.png" alt="Multi-team health check board" />

        <ExpandableCallout type="info" title="Remember to:" className="mt-4">
          <p className="text-gray-200 mb-4">
            Leave a pile of dot votes to the left hand side of each board and to also include some simple facilitation instructions:
          </p>
          <PlaybookImage src="https://auth.openorg.fyi/storage/v1/object/public/resources/team-health-check/instructions.png" alt="Facilitation instructions" />
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Action: Brief Your Facilitators</h3>
        <p className="text-gray-200 mb-4">
          Now that your board is prepared. It's time to brief your facilitators. This is best done as a pre-read followed by a group call to encourage Q&A, I'd recommend 25mins.
        </p>

        <CopyToNotion title="Notion Memo Template">
          <div className="space-y-4">
            <p>Hey facilitators!</p>
            
            <p>In preparation for our call next week on running our first team health check, I wanted to share our main board that we'll be using to run our first team health check.</p>
            
            <p>Here's the board link</p>
            
            <p>Please can you check out the facilitation guidance as a pre-read so that we can spend most of the time on the call tackling any questions around how to facilitate this session.</p>
            
            <p>Thanks,</p>
            <p>- Your name</p>
          </div>
        </CopyToNotion>

        <p className="text-gray-200 mt-4 mb-4">On the call it's a good chance to check in on confidence levels and facilitation skill. The main outcomes are:</p>

        <ul className="list-none space-y-2 mb-6">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Facilitators understand how to run the session</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Experienced facilitators can share best practice/tips with others (it's not all on you)</span>
          </li>
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Commit to booking in their session with their team</span>
          </li>
        </ul>

        <ExpandableCallout type="info" title="Scheduling tip:" className="mt-4">
          <p className="text-gray-200">
            It's a good idea to try and co-ordinate all team health checks across all teams to happen within the same week. This makes the next stage much easier and also means that there won't be additional delays in surfacing the insights and their required follow ups.
          </p>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">After the Call</h3>
        <p className="text-gray-200">
          You'll have a sense of who might benefit from you personally pairing with them on the facilitation, some won't need or want it, one or two might.
        </p>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Facilitation</h3>
        <p className="text-gray-200 mb-4">
          If you are facilitating one or more of the team's first session. Here's a short guide:
        </p>

        <ExpandableCallout type="info" title="John's facilitation blueprint:" className="mt-4">
          <div className="space-y-4">
            <p className="text-gray-200">1. Share the board with the team beforehand as a refresher and create a short loom (90s) that walks through the guidelines on the board. You could create a mini agenda like this and talk to it:</p>
            
            <ol className="list-decimal pl-6 space-y-2">
              <li>We're going to refresh on our team health check dials and review each definition of awesome and definition of dumpster fire (10mins)</li>
              <li>Next we'll pop on some music for and dot vote on each dial providing one sticky note comment as to why we voted what we voted (20mins)</li>
              <li>Have some space for reflection and ideation on things (30mins)
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>we can action</li>
                  <li>we might need some help actioning</li>
                </ul>
              </li>
            </ol>

            <p className="text-gray-200 mt-4">// on the day</p>
            
            <p className="text-gray-200">
              This is very much retrospective vibes. Inclusivity and reflection are the 2 words to have top of mind.
            </p>

            <p className="text-gray-200">
              Follow the above steps, keep the tempo up and focus on getting to a place where:
            </p>

            <p className="text-gray-200">
              Everyone has contributed their dot vote for each dial and a comment on why they voted as such + a suggestion that the team can do to make that improve.
            </p>

            <p className="text-gray-200">
              If you do have space, then a quick prioritisation of the things the team want to action themselves is a great place to finish.
            </p>
          </div>
        </ExpandableCallout>
      </PlaybookCard>

      <PlaybookCard>
        <h3 className="text-2xl font-semibold text-white mb-4">Outcome</h3>
        <p className="text-gray-200 mb-4">
          After each team has run their health check you should have a:
        </p>
        <ul className="list-none space-y-2">
          <li className="flex items-start">
            <span className="text-primary-light mr-2">—</span>
            <span className="text-gray-200">Completed team health check board for each team with dot votes, comments and ideas to improve things ideally split into: things the team can and will action, things they need support on</span>
          </li>
        </ul>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default TeamHealthChecksStage2; 